import { analytics } from '../config/firebase';
import { logEvent, setUserId, setUserProperties, Analytics } from 'firebase/analytics';

export class FirebaseAnalytics {
  private static instance: FirebaseAnalytics;
  private isInitialized = false;

  private constructor() {}

  static getInstance(): FirebaseAnalytics {
    if (!FirebaseAnalytics.instance) {
      FirebaseAnalytics.instance = new FirebaseAnalytics();
    }
    return FirebaseAnalytics.instance;
  }

  initialize() {
    if (analytics && !this.isInitialized) {
      this.isInitialized = true;
      if (process.env.NODE_ENV === 'development') {
        console.debug('Firebase Analytics initialized');
      }
    } else if (!analytics && process.env.NODE_ENV === 'development') {
      console.debug('Firebase Analytics not available - configuration missing');
    }
  }

  private getAnalytics(): Analytics | null {
    return analytics;
  }

  // Track page views
  trackPageView(pageName: string, pageTitle?: string) {
    const analyticsInstance = this.getAnalytics();
    if (!analyticsInstance || !this.isInitialized) return;
    
    logEvent(analyticsInstance, 'page_view', {
      page_name: pageName,
      page_title: pageTitle || pageName,
    });
  }

  // Track user login
  trackLogin(method: string) {
    const analyticsInstance = this.getAnalytics();
    if (!analyticsInstance || !this.isInitialized) return;
    
    logEvent(analyticsInstance, 'login', {
      method: method,
    });
  }

  // Track user registration
  trackSignUp(method: string) {
    const analyticsInstance = this.getAnalytics();
    if (!analyticsInstance || !this.isInitialized) return;
    
    logEvent(analyticsInstance, 'sign_up', {
      method: method,
    });
  }

  // Track authentication events
  trackAuthEvent(event: 'login' | 'logout' | 'register' | 'password_reset', userId?: string) {
    const analyticsInstance = this.getAnalytics();
    if (!analyticsInstance || !this.isInitialized) return;
    
    logEvent(analyticsInstance, 'auth_event', {
      event: event,
      user_id: userId,
    });
  }

  // Track timesheet actions
  trackTimesheetAction(action: 'create' | 'update' | 'delete' | 'view') {
    const analyticsInstance = this.getAnalytics();
    if (!analyticsInstance || !this.isInitialized) return;
    
    logEvent(analyticsInstance, 'timesheet_action', {
      action: action,
    });
  }

  // Track task actions
  trackTaskAction(action: 'create' | 'update' | 'delete' | 'complete') {
    const analyticsInstance = this.getAnalytics();
    if (!analyticsInstance || !this.isInitialized) return;
    
    logEvent(analyticsInstance, 'task_action', {
      action: action,
    });
  }

  // Track organization actions
  trackOrganizationAction(action: 'create' | 'join' | 'leave' | 'update') {
    const analyticsInstance = this.getAnalytics();
    if (!analyticsInstance || !this.isInitialized) return;
    
    logEvent(analyticsInstance, 'organization_action', {
      action: action,
    });
  }

  // Track subscription events
  trackSubscriptionEvent(event: 'view' | 'upgrade' | 'downgrade' | 'cancel') {
    const analyticsInstance = this.getAnalytics();
    if (!analyticsInstance || !this.isInitialized) return;
    
    logEvent(analyticsInstance, 'subscription_event', {
      event: event,
    });
  }

  // Track error events with detailed information
  trackError(error: string, errorCode?: string, errorDetails?: any) {
    const analyticsInstance = this.getAnalytics();
    if (!analyticsInstance || !this.isInitialized) return;
    
    // Capture stack trace if available
    let stackTrace = '';
    if (errorDetails instanceof Error) {
      stackTrace = errorDetails.stack || '';
    } else if (errorDetails && typeof errorDetails === 'object') {
      stackTrace = errorDetails.stack || '';
    }
    
    // Capture additional context
    const errorContext = {
      error_message: error,
      error_code: errorCode,
      stack_trace: stackTrace,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      url: window.location.href,
      referrer: document.referrer,
    };
    
    // Add any additional error details
    if (errorDetails && typeof errorDetails === 'object') {
      Object.assign(errorContext, {
        error_details: JSON.stringify(errorDetails),
        error_type: errorDetails.constructor?.name || typeof errorDetails,
      });
    }
    
    logEvent(analyticsInstance, 'error', errorContext);
    
    // Also log to console for debugging
    console.error('Firebase Error Tracking:', {
      message: error,
      code: errorCode,
      stack: stackTrace,
      details: errorDetails,
    });
  }

  // Track authentication errors with detailed context
  trackAuthError(error: string, errorCode: string, errorDetails?: any, context?: {
    email?: string;
    action?: string;
    timestamp?: string;
  }) {
    const analyticsInstance = this.getAnalytics();
    if (!analyticsInstance || !this.isInitialized) return;
    
    // Capture stack trace
    let stackTrace = '';
    if (errorDetails instanceof Error) {
      stackTrace = errorDetails.stack || '';
    } else if (errorDetails && typeof errorDetails === 'object') {
      stackTrace = errorDetails.stack || '';
    }
    
    const authErrorContext = {
      error_message: error,
      error_code: errorCode,
      stack_trace: stackTrace,
      timestamp: context?.timestamp || new Date().toISOString(),
      user_agent: navigator.userAgent,
      url: window.location.href,
      action: context?.action || 'unknown',
      email_hash: context?.email ? this.hashEmail(context.email) : '',
    };
    
    // Add error details if available
    if (errorDetails && typeof errorDetails === 'object') {
      Object.assign(authErrorContext, {
        error_details: JSON.stringify(errorDetails),
        error_type: errorDetails.constructor?.name || typeof errorDetails,
      });
    }
    
    logEvent(analyticsInstance, 'auth_error', authErrorContext);
    
    // Also log to console for debugging
    console.error('Firebase Auth Error Tracking:', {
      message: error,
      code: errorCode,
      action: context?.action,
      stack: stackTrace,
      details: errorDetails,
    });
  }

  // Hash email for privacy (simple hash for analytics)
  private hashEmail(email: string): string {
    let hash = 0;
    for (let i = 0; i < email.length; i++) {
      const char = email.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  // Set user ID for tracking
  setUserId(userId: string) {
    const analyticsInstance = this.getAnalytics();
    if (!analyticsInstance || !this.isInitialized) return;
    
    setUserId(analyticsInstance, userId);
  }

  // Set user properties
  setUserProperties(properties: Record<string, string>) {
    const analyticsInstance = this.getAnalytics();
    if (!analyticsInstance || !this.isInitialized) return;
    
    setUserProperties(analyticsInstance, properties);
  }

  // Track custom events
  trackCustomEvent(eventName: string, parameters?: Record<string, any>) {
    const analyticsInstance = this.getAnalytics();
    if (!analyticsInstance || !this.isInitialized) return;
    
    logEvent(analyticsInstance, eventName, parameters);
  }
}

export const firebaseAnalytics = FirebaseAnalytics.getInstance();
